import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/extraServices/extra-services-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {extraServicesRuntimeStore, runtimeStore} from '~/composables/store-accessor'
import {isAfter, isBefore, parseISO} from 'date-fns'
import {UTCToDate} from '@/utils/helpers'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import type {Ref} from 'vue'
import {useProductStore} from '~/composables/product-store'

export const useExtraServicesStore = defineStore('extraServices', () => {
    const searchResponse: Ref = ref(searchResponseBlank())
    const searchRequest: Ref = ref(searchRequestBlank())
    const filters: Ref = ref(filtersBlank())
    const sortFnName: Ref = ref('priceAsc')
    const searchExpirationTime: Ref = ref(null)
    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse: Ref = ref({})
    const basket: Ref<any[]> = ref([])
    const bookingAdditionalOptions: Ref<any[]> = ref([])

    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find((item: any) => item.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                product: {offers: [], info: {}},
                info: {},
            }
        )
    }

    function ADD_TO_BASKET({info, offerKey}: any) {
        basket.value = basket.value.filter(item => {
            const offer = item.product.offers.find((offer: any) => offer.offerKey === item.offerKey)
            return isBefore(new Date(), UTCToDate(parseISO(offer.expirationTime)))
        })
        const basketItem = basket.value.find(item => item.offerKey === offerKey) || {
            searchRequest: searchRequestBlank(),
            products: {offers: [], info: {}},
            info: {},
        }
        if (basketItem.offerKey) return
        const product = extraServicesRuntimeStore.offers.find(
            (product: any) => product.offers.findIndex((offer: any) => offer.offerKey === offerKey) !== -1
        )
        basket.value.push({
            offerKey,
            product,
            info,
            searchRequest: searchRequest.value,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.product.offers.find((offer: any) => offer.offerKey === offerKey)
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)

        offer.currentPrice = price
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_FILTER(data: any) {
        filters.value[data.key] = data.value
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function SET_SORT(sort: any) {
        sortFnName.value = sort
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP(data: any) {
        prepareBookRequest.value[data.prop] = data.val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        filters.value = filtersBlank()
    }

    function RESET() {
        searchRequest.value = searchRequestBlank()
        prepareBookRequest.value = {}
    }

    function SET_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        if (Object.keys(options).length) {
            bookingAdditionalOptions.value = options
        }
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            //TODO API workaround - ignore params in API layer
            rq.tourists?.forEach((tourist: any) => {
                if (tourist.passport) {
                    delete tourist.passport
                }
                if (tourist.citizenshipId) {
                    delete tourist.citizenshipId
                }
                if (tourist.phone) {
                    delete tourist.phone
                }
                if (tourist.email) {
                    delete tourist.email
                }
            })
            SET_PREPARE_BOOK_REQUEST(rq)
            const rs = await appInstance.$api.prepareOwnExtraServiceBook.post(rq)
            SET_PREPARE_BOOK_RESPONSE(rs)
            return rs
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = []
    }

    async function getExtraServiceAdditionalOptions(offerKey: any) {
        try {
            const res = await appInstance.$api.ownExtraServiceAdditionalOptions.get({offerKey})
            SET_BOOKING_ADDITIONAL_OPTIONS(res)
        } catch (error) {
            console.error(error)
        }
    }

    const isOffersExpired = computed(() => {
        return () => hasOffers.value && isAfter(new Date(), searchExpirationTime.value)
    })

    const basketItem = computed(() => {
        return (offerKey: any) => _getBasketItem.call(this, offerKey)
    })

    const hasOffers = computed(() => {
        return !!searchResponse.value.products.length
    })

    return {
        ...useProductStore(searchResponse),
        searchRequest,
        searchResponse,
        filters,
        sortFnName,
        searchExpirationTime,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        bookingAdditionalOptions,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        SET_SEARCH_REQUEST,
        SET_FILTERS,
        SET_FILTER,
        RESET_FILTERS,
        SET_SORT,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        NEW_SEARCH,
        RESET,
        SET_BOOKING_ADDITIONAL_OPTIONS,
        prepareBook,
        clearBookingAdditionalOptions,
        getExtraServiceAdditionalOptions,
        isOffersExpired,
        basketItem,
        hasOffers,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useExtraServicesStore, import.meta.hot))
